import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { Box, Typography, Paper, Alert, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { Trash2Icon } from 'lucide-react';
import UnifiedTransactionModal from '../shared/UnifiedTransactionModal';
import UnifiedTransactionTable from '../shared/UnifiedTransactionTable';
interface Transaction {
  id: string;
  senderName: string;
  receiverName: string;
  amount: number;
  currency: string;
  payoutAmount: number;
  payoutCurrency: string;
  date: string;
  status: string;
  paymentMethod: string;
  mto: string;
  type: string;
  branchCode: string;
  agentCode?: string;
  createdAt?: string;
  rejectionReason?: string;
  rejectedAt?: string;
  rejectedBy?: string;
  parties?: any;
  accounts?: any;
  overview?: any;
  idType?: string;
  idNumber?: string;
  veresysBioSys?: boolean;
  ofacUnscVerification?: boolean;
  contactNumber?: string;
  receiverCountry?: string;
  province?: string;
  city?: string;
  idExpiry?: string;
  email?: string;
  dateOfBirth?: string;
  zipCode?: string;
  address?: string;
}
const TransactionApprovalHistoryPage: React.FC = () => {
  const [rejectedTransactions, setRejectedTransactions] = useState<Transaction[]>([]);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState<Transaction | null>(null);
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };
  const loadRejectedTransactions = () => {
    const stored = localStorage.getItem('initiatedTransactions');
    if (stored) {
      const allTransactions = JSON.parse(stored);
      const rejected = allTransactions.filter((t: any) => t.status === 'Rejected' || t.checkerDecision === 'rejected');
      const formattedRejected: Transaction[] = rejected.map((t: any) => ({
        id: t.id || '',
        senderName: t.senderName || t.parties?.sender?.name || 'Unknown',
        receiverName: t.receiverName || t.parties?.receiver?.name || 'Unknown',
        amount: t.amount || t.amounts?.sendAmount || 0,
        currency: t.currency || t.amounts?.sendCurrency || 'AED',
        payoutAmount: t.payoutAmount || t.amounts?.payoutAmount || 0,
        payoutCurrency: t.payoutCurrency || t.amounts?.payoutCurrency || 'PKR',
        date: t.date || new Date().toISOString().split('T')[0],
        status: t.status || 'Rejected',
        paymentMethod: t.paymentMethod || '',
        mto: t.agent || t.mto || 'Unknown',
        type: 'OTC',
        branchCode: t.branchCode || '',
        agentCode: t.agentCode,
        createdAt: t.createdAt || new Date().toISOString(),
        rejectionReason: t.rejectionReason || t.checkerReason || 'No reason provided',
        rejectedAt: t.rejectedAt,
        idType: t.idType,
        idNumber: t.idNumber,
        veresysBioSys: t.veresysBioSys,
        ofacUnscVerification: t.ofacUnscVerification,
        contactNumber: t.contactNumber,
        receiverCountry: t.receiverCountry,
        province: t.province,
        city: t.city,
        idExpiry: t.idExpiry,
        email: t.email,
        dateOfBirth: t.dateOfBirth,
        zipCode: t.zipCode,
        address: t.address,
        parties: t.parties,
        accounts: t.accounts,
        overview: t.overview
      }));
      setRejectedTransactions(formattedRejected);
    }
  };
  useEffect(() => {
    loadRejectedTransactions();
    const handleUpdate = () => {
      loadRejectedTransactions();
    };
    window.addEventListener('transactionUpdated', handleUpdate);
    window.addEventListener('transactionAdded', handleUpdate);
    return () => {
      window.removeEventListener('transactionUpdated', handleUpdate);
      window.removeEventListener('transactionAdded', handleUpdate);
    };
  }, []);
  const handleViewDetails = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowModal(true);
  };
  const handleDeleteClick = (transaction: Transaction, event: React.MouseEvent) => {
    event.stopPropagation();
    setTransactionToDelete(transaction);
    setShowDeleteDialog(true);
  };
  const handleDeleteTransaction = () => {
    if (!selectedTransaction) return;
    const stored = localStorage.getItem('initiatedTransactions');
    if (!stored) return;
    const allTransactions = JSON.parse(stored);
    const updatedTransactions = allTransactions.filter((t: any) => t.id !== selectedTransaction.id);
    localStorage.setItem('initiatedTransactions', JSON.stringify(updatedTransactions));
    window.dispatchEvent(new Event('transactionUpdated'));
    setShowModal(false);
    setSelectedTransaction(null);
    loadRejectedTransactions();
  };
  const handleDeleteConfirm = () => {
    if (!transactionToDelete) return;
    const stored = localStorage.getItem('initiatedTransactions');
    if (!stored) return;
    const allTransactions = JSON.parse(stored);
    const updatedTransactions = allTransactions.filter((t: any) => t.id !== transactionToDelete.id);
    localStorage.setItem('initiatedTransactions', JSON.stringify(updatedTransactions));
    window.dispatchEvent(new Event('transactionUpdated'));
    setShowDeleteDialog(false);
    setTransactionToDelete(null);
    loadRejectedTransactions();
  };
  const handleResubmit = (reason: string, updatedData: any) => {
    if (!selectedTransaction) return;
    const stored = localStorage.getItem('initiatedTransactions');
    if (!stored) return;
    const allTransactions = JSON.parse(stored);
    const updatedTransactions = allTransactions.map((t: any) => {
      if (t.id === selectedTransaction.id) {
        return {
          ...t,
          status: 'Pending Checker Approval',
          checkerDecision: null,
          checkerReason: null,
          rejectionReason: null,
          rejectedAt: null,
          rejectedBy: null,
          resubmittedAt: new Date().toISOString(),
          resubmissionReason: reason,
          // Update ID Verification fields
          idType: updatedData.idType,
          idNumber: updatedData.idNumber,
          veresysBioSys: updatedData.veresysBioSys,
          ofacUnscVerification: updatedData.ofacUnscVerification,
          contactNumber: updatedData.contactNumber,
          receiverCountry: updatedData.receiverCountry,
          province: updatedData.province,
          city: updatedData.city,
          idExpiry: updatedData.idExpiry,
          email: updatedData.email,
          dateOfBirth: updatedData.dateOfBirth,
          zipCode: updatedData.zipCode,
          address: updatedData.address,
          overview: {
            ...t.overview,
            status: 'Pending Checker Approval'
          }
        };
      }
      return t;
    });
    localStorage.setItem('initiatedTransactions', JSON.stringify(updatedTransactions));
    window.dispatchEvent(new Event('transactionUpdated'));
    setShowModal(false);
    setSelectedTransaction(null);
    loadRejectedTransactions();
  };
  const columns = [{
    id: 'id',
    label: 'Transaction ID',
    filterable: true,
    filterType: 'text' as const
  }, {
    id: 'date',
    label: 'Date',
    filterable: true,
    filterType: 'date' as const
  }, {
    id: 'amount',
    label: 'Amount FCY',
    filterable: true,
    filterType: 'number' as const,
    format: (value: number) => formatCurrency(value, 'AED')
  }, {
    id: 'payoutAmount',
    label: 'Amount PKR',
    format: (value: number) => formatCurrency(value, 'PKR')
  }, {
    id: 'mto',
    label: 'MTO',
    filterable: true,
    filterType: 'select' as const,
    filterOptions: ['UAE Exchange', 'Western Union', 'MoneyGram', 'Ria Money Transfer', 'Al Ansari Exchange']
  }, {
    id: 'type',
    label: 'Type'
  }, {
    id: 'branchCode',
    label: 'Branch Code',
    filterable: true,
    filterType: 'text' as const
  }, {
    id: 'agentCode',
    label: 'Agent Code'
  }, {
    id: 'createdAt',
    label: 'Created',
    format: (value: string) => value ? new Date(value).toLocaleString() : '—'
  }, {
    id: 'paymentMethod',
    label: 'Payment Method',
    format: (value: string) => value || '—'
  }, {
    id: 'status',
    label: 'Status',
    filterable: true,
    filterType: 'select' as const,
    filterOptions: ['Rejected'],
    format: (value: string) => <Chip label={value} color="error" size="small" data-id="element-1101" />
  }, {
    id: 'actions',
    label: 'Delete',
    format: (value: any, row: Transaction) => <IconButton size="small" onClick={e => handleDeleteClick(row, e)} sx={{
      color: 'error.main'
    }} data-id="element-1102">
          <Trash2Icon size={18} data-id="element-1103" />
        </IconButton>
  }];
  return <Box sx={{
    p: 3,
    bgcolor: 'background.default',
    minHeight: '100vh'
  }} data-id="element-1104">
      <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      mb: 3
    }} data-id="element-1105">
        <Icon icon="lucide:history" width={28} height={28} color="#0052CC" data-id="element-1106" />
        <Typography variant="h4" sx={{
        fontWeight: 700,
        color: '#0052CC'
      }} data-id="element-1107">
          Transaction Maker Inbox
        </Typography>
      </Box>

      {rejectedTransactions.length === 0 ? <Paper elevation={1} sx={{
      p: 6,
      textAlign: 'center'
    }} data-id="element-1108">
          <Icon icon="lucide:check-circle-2" width={64} height={64} color="#0B7923" style={{
        marginBottom: 16
      }} data-id="element-1109" />
          <Typography variant="h6" color="text.secondary" gutterBottom data-id="element-1110">
            No Rejected Transactions
          </Typography>
          <Typography variant="body2" color="text.secondary" data-id="element-1111">
            All transactions are either approved or pending approval.
          </Typography>
        </Paper> : <>
          <Alert severity="error" sx={{
        mb: 3
      }} data-id="element-1112">
            <Typography variant="body2" fontWeight={600} data-id="element-1113">
              You have {rejectedTransactions.length} rejected transaction
              {rejectedTransactions.length !== 1 ? 's' : ''} that require
              resubmission or deletion.
            </Typography>
          </Alert>

          <UnifiedTransactionTable columns={columns} data={rejectedTransactions} onViewDetails={handleViewDetails} title={`Rejected Transactions (${rejectedTransactions.length})`} emptyMessage="No rejected transactions found" data-id="element-1114" />
        </>}

      {selectedTransaction && <UnifiedTransactionModal transaction={selectedTransaction} isOpen={showModal} onClose={() => {
      setShowModal(false);
      setSelectedTransaction(null);
    }} mode="resubmit" onResubmit={handleResubmit} onDelete={handleDeleteTransaction} data-id="element-1115" />}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} maxWidth="sm" fullWidth data-id="element-1116">
        <DialogTitle data-id="element-1117">
          <Typography variant="h6" color="error.main" data-id="element-1118">
            Delete Transaction
          </Typography>
        </DialogTitle>
        <DialogContent data-id="element-1119">
          <Alert severity="warning" sx={{
          mb: 2
        }} data-id="element-1120">
            This action cannot be undone!
          </Alert>
          <Typography variant="body2" color="text.secondary" data-id="element-1121">
            Are you sure you want to delete transaction{' '}
            <strong data-id="element-1122">{transactionToDelete?.id}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{
          mt: 2
        }} data-id="element-1123">
            This will permanently remove the transaction from the system.
          </Typography>
        </DialogContent>
        <DialogActions sx={{
        p: 2
      }} data-id="element-1124">
          <Button onClick={() => setShowDeleteDialog(false)} data-id="element-1125">Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} data-id="element-1126">
            Delete Transaction
          </Button>
        </DialogActions>
      </Dialog>
    </Box>;
};
export default TransactionApprovalHistoryPage;